import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SkillDevelopmentHero from "../components/CareersComponents/SkillDevelopment/SkillDevelopmentHero";
import SkillCourseList from "../components/CareersComponents/SkillDevelopment/SkillCourseList";
import SkillCoursePagination from "../components/CareersComponents/SkillDevelopment/SkillCoursePagination";
import CareersFooter from "../components/CareersComponents/CareersFooter";
import { Box, Spinner, Center, Text, Tabs, TabList, TabPanels, Tab, TabPanel, Icon, SimpleGrid, Button, Flex } from "@chakra-ui/react";
import { FaClock, FaGraduationCap, FaPlay } from "react-icons/fa";
import { useCategorywiseCourses, useUpcomingCourses, useTrainingCategories } from "../hooks/useSkillDevelopment";

// const defaultFilter = {
//   keyword: "",
//   category: "",
//   location: "",
//   medium: "",
//   type: [],
//   level: [],
//   fee: [0, 2000],
// };

const PAGE_SIZE = 6;

const SkillDevelopment = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Fetch upcoming courses
  const { courses: upcomingCourses, loading: upcomingLoading, error: upcomingError } = useUpcomingCourses();
  
  // Fetch training categories
  const { categories, loading: categoriesLoading, error: categoriesError } = useTrainingCategories();
  
  // Fetch courses based on selected category
  const { courses: apiCourses, loading: coursesLoading, error: coursesError } = useCategorywiseCourses(selectedCategory?.encodedId);
  
  // Debug logging
  React.useEffect(() => {
    }, [upcomingCourses, upcomingLoading, upcomingError, categories, categoriesLoading, categoriesError, selectedCategory, apiCourses, coursesLoading, coursesError]);
  
  // Transform API data to match component expectations
  const transformCourse = (apiCourse) => {
    // Fix image URL - keep original URL handling for now
    let imageUrl = apiCourse.course_thumbnail;
    if (imageUrl && imageUrl.includes('localhost:8080')) {
      imageUrl = imageUrl.replace('http://localhost:8080', process.env.REACT_APP_EBS_API_BASE_URL);
    }
    
    return {
      id: apiCourse.training_id,
      training_id: apiCourse.training_id, // Preserve the original training_id
      title: apiCourse.batch_name || apiCourse.course_name,
      batch_name: apiCourse.batch_name, // Preserve original batch_name
      batch_type: apiCourse.batch_type, // Preserve original batch_type
      amount: apiCourse.amount, // Preserve original batch_name
      course_name: apiCourse.course_name, // Preserve original course_name
      course_thumbnail: imageUrl, // Keep both image and course_thumbnail
      image: imageUrl,
      category: "Training", // You can set this based on the selected category
      location: apiCourse.venue_name,
      venue_name: apiCourse.venue_name, // Preserve original venue_name
      duration: apiCourse.duration,
      fee: 0, // API doesn't provide fee, assuming free for now
      deadline: apiCourse.deadline,
      start_date: apiCourse.start_date, // Preserve original start_date
      end_date: apiCourse.end_date, // Preserve original end_date
      startDate: apiCourse.start_date,
      endDate: apiCourse.end_date,
      status: apiCourse.batch_status,
      batch_status: apiCourse.batch_status, // Preserve original batch_status
      objective: apiCourse.training_objective,
      training_objective: apiCourse.training_objective, // Preserve original
      outline: apiCourse.training_outline,
      training_outline: apiCourse.training_outline, // Preserve original
      created_at: apiCourse.created_at, // Include created_at
      medium: apiCourse.venue_name === "Online" ? "Online" : "Offline",
      type: ["training"],
      level: ["All Levels"]
    };
  };

  // Filter API courses based on other filter criteria (simplified for now)
  const getFilteredCourses = () => {
    if (!apiCourses || apiCourses.length === 0) return [];
    return apiCourses.map(transformCourse);
  };

  const filteredCourses = getFilteredCourses();

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Calculate pagination values
  const totalItems = filteredCourses.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const showingStart = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingEnd = Math.min(currentPage * PAGE_SIZE, totalItems);
  const pagedCourses = filteredCourses.slice(showingStart - 1, showingEnd);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  // Category Card Component
  const CategoryCard = ({ category, courseCount }) => (
    <Box
      bg="white"
      borderRadius={{ base: "8px", md: "12px" }}
      p={{ base: 4, md: 6 }}
      boxShadow="sm"
      border="1px solid #E6EAF0"
      cursor="pointer"
      onClick={() => handleCategorySelect(category)}
      _hover={{
        boxShadow: "md",
        transform: "translateY(-2px)",
        borderColor: "#5A9BD4"
      }}
      transition="all 0.2s"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexDirection={{ base: "column", sm: "row" }}
      textAlign={{ base: "center", sm: "left" }}
      h={{ base: "auto", sm: "80px" }}
      w="100%"
      minH={{ base: "60px", sm: "80px" }}
      maxH={{ base: "none", sm: "80px" }}
      py={{ base: 4, sm: 3 }}
      gap={{ base: 2, sm: 0 }}
    >
      <Box 
        display="flex" 
        alignItems="center" 
        flex="1" 
        minW="0"
        justifyContent={{ base: "center", sm: "flex-start" }}
        mb={{ base: 2, sm: 0 }}
      >
        <Icon 
          as={FaPlay} 
          color="#5A9BD4" 
          mr={{ base: 2, sm: 3 }} 
          flexShrink={0}
          fontSize={{ base: "sm", md: "md" }}
        />
        <Text 
          fontWeight="600" 
          color="#1A202C" 
          fontSize={{ base: "sm", md: "md" }}
          noOfLines={{ base: 3, sm: 2 }}
          lineHeight="1.3"
        >
          {category.category_name || category.name}
        </Text>
      </Box>
      <Text 
        color="#E53E3E" 
        fontWeight="bold" 
        fontSize={{ base: "md", md: "lg" }}
        flexShrink={0}
        ml={{ base: 0, sm: 3 }}
      >
        {courseCount}
      </Text>
    </Box>
  );

  return (
    <>
      <SkillDevelopmentHero />
      
      {/* Training Course Tabs */}
      <Box px={{ base: 4, sm: 6, md: 16, lg: 32 }} pt={{ base: 6, md: 8 }} bg="#F8F9FA" position="relative">
        
        <Tabs variant="unstyled" isFitted>
          <Box position="relative">
            <TabList 
              bg="white" 
              borderRadius={{ base: "25px", md: "50px" }}
              p={{ base: 1, md: 2 }}
              boxShadow="sm"
              maxW={{ base: "100%", sm: "80%", md: "600px" }}
              mx="auto"
              flexDirection={{ base: "column", sm: "row" }}
              gap={{ base: 1, sm: 0 }}
            >
            <Tab
              borderRadius={{ base: "25px", md: "50px" }}
              color="#7A869A"
              fontWeight="600"
              fontSize={{ base: "sm", md: "md" }}
              py={{ base: 2, md: 3 }}
              px={{ base: 4, md: 6 }}
              _selected={{
                bg: "#5A9BD4",
                color: "white",
                boxShadow: "sm"
              }}
              _hover={{
                bg: "#E8F4FD",
                color: "#5A9BD4"
              }}
              transition="all 0.2s"
              width={{ base: "100%", sm: "auto" }}
            >
              <Icon as={FaClock} mr={2} fontSize={{ base: "sm", md: "md" }} />
              <Text fontSize={{ base: "xs", sm: "sm", md: "md" }}>
                Upcoming Training Course
              </Text>
            </Tab>
            <Tab
              borderRadius={{ base: "25px", md: "50px" }}
              color="#7A869A"
              fontWeight="600"
              fontSize={{ base: "sm", md: "md" }}
              py={{ base: 2, md: 3 }}
              px={{ base: 4, md: 6 }}
              _selected={{
                bg: "#5A9BD4",
                color: "white",
                boxShadow: "sm"
              }}
              _hover={{
                bg: "#E8F4FD",
                color: "#5A9BD4"
              }}
              transition="all 0.2s"
              width={{ base: "100%", sm: "auto" }}
            >
              <Icon as={FaGraduationCap} mr={2} fontSize={{ base: "sm", md: "md" }} />
              <Text fontSize={{ base: "xs", sm: "sm", md: "md" }}>
                All Training Course
              </Text>
            </Tab>
            </TabList>
          </Box>

          <TabPanels>
            {/* Upcoming Training Course Tab */}
            <TabPanel p={0} pt={{ base: 4, md: 6 }}>
              {upcomingLoading && (
                <Center py={{ base: 8, md: 10 }}>
                  <Spinner size={{ base: "lg", md: "xl" }} color="#235B63" />
                </Center>
              )}
              
              {upcomingError && (
                <Center py={{ base: 8, md: 10 }}>
                  <Text color="red.500" fontSize={{ base: "md", md: "lg" }} textAlign="center" px={4}>
                    Error loading upcoming courses: {upcomingError}
                  </Text>
                </Center>
              )}
              
              {!upcomingLoading && !upcomingError && upcomingCourses.length > 0 && (
                <SkillCourseList courses={upcomingCourses.map(transformCourse)} />
              )}
              
              {!upcomingLoading && !upcomingError && upcomingCourses.length === 0 && (
                <Center py={{ base: 8, md: 10 }}>
                  <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" textAlign="center" px={4}>
                    No upcoming training courses available
                  </Text>
                </Center>
              )}
            </TabPanel>

            {/* All Training Course Tab */}
            <TabPanel p={0} pt={{ base: 4, md: 8 }}>
              {!selectedCategory ? (
                // Show Categories
                <>
                  {categoriesLoading && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Spinner size={{ base: "lg", md: "xl" }} color="#235B63" />
                    </Center>
                  )}
                  
                  {categoriesError && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Text color="red.500" fontSize={{ base: "md", md: "lg" }} textAlign="center" px={4}>
                        Error loading categories: {categoriesError}
                      </Text>
                    </Center>
                  )}
                  
                  {!categoriesLoading && !categoriesError && categories.length > 0 && (
                    <SimpleGrid 
                      columns={{ base: 1, sm: 1, md: 2, lg: 2 }} 
                      spacing={{ base: 3, md: 4 }} 
                      px={{ base: 2, md: 4 }}
                      maxW="800px"
                      mx="auto"
                    >
                      {categories.map((category, index) => (
                        <CategoryCard 
                          key={category.encodedId || index} 
                          category={category} 
                          courseCount={category.batch_count || 0}
                        />
                      ))}
                    </SimpleGrid>
                  )}
                  
                  {!categoriesLoading && !categoriesError && categories.length === 0 && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" textAlign="center" px={4}>
                        No training categories available
                      </Text>
                    </Center>
                  )}
                </>
              ) : (
                // Show Courses for Selected Category
                <>
                  <Box mb={{ base: 4, md: 6 }} px={{ base: 2, md: 4 }}>
                    <Button
                      onClick={() => setSelectedCategory(null)}
                      variant="outline"
                      colorScheme="blue"
                      size={{ base: "sm", md: "md" }}
                      mb={{ base: 3, md: 4 }}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      ← Back to Categories
                    </Button>
                    <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="#1A202C">
                      {selectedCategory.name} Courses
                    </Text>
                  </Box>
                  
                  {coursesLoading && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Spinner size={{ base: "lg", md: "xl" }} color="#235B63" />
                    </Center>
                  )}
                  
                  {coursesError && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Text color="red.500" fontSize={{ base: "md", md: "lg" }} textAlign="center" px={4}>
                        Error loading courses: {coursesError}
                      </Text>
                    </Center>
                  )}

                  {!coursesLoading && !coursesError && filteredCourses.length > 0 && (
                    <>
                      <SkillCourseList courses={pagedCourses} />
                      <Box px={{ base: 2, sm: 4, md: 16, lg: 32 }} >
                        <SkillCoursePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          pageSize={PAGE_SIZE}
                          totalItems={totalItems}
                          onPageChange={handlePageChange}
                          showingStart={showingStart}
                          showingEnd={showingEnd}
                        />
                      </Box>
                    </>
                  )}
                  
                  {!coursesLoading && !coursesError && apiCourses.length === 0 && (
                    <Center py={{ base: 8, md: 10 }}>
                      <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" textAlign="center" px={4}>
                        No courses found for this category
                      </Text>
                    </Center>
                  )}
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <CareersFooter />
    </>
  );
};export default SkillDevelopment; 